import _, {compact, first, last, map} from 'lodash';
import {transparentize} from 'polished';
import React, {
  PropsWithChildren,
  RefObject,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled, {keyframes} from 'styled-components';

import {computeParentElementFloatingActivity} from '../../../../libs/shared/core/src/helpers/annotations/annotationDomHelpers';
import {
  annotationHtmlAttribute,
  annotationLinkedTypeAttribute,
  FloatingActivity,
  FloatingActivityTypesEnum,
  htmlAnnotationTagName,
} from '../../../../libs/shared/core/src/helpers/annotations/annotationHelpers';
import {
  copySelectionToClipboard,
  isElement,
} from '../../../../libs/shared/core/src/helpers/browser/domHelpers';
import {
  isSelectionCollapsed,
  normalizeAndCreateSelectionLocationFromSelection,
  SelectionLocation,
} from '../../../../libs/shared/core/src/helpers/browser/domSelectionHelpers';
import {hasMetaKey} from '../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {useEventHandler} from '../../../../libs/shared/core/src/helpers/react/hooks/useEventHandler';
import {FrontMouseEvent} from '../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {isInteger} from '../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {
  AnnotationModel,
  isAnnotationUid,
  maybeFindAnnotationLinkedActivityId,
} from '../../../../libs/shared/core/src/models/api/annotationModel';
import {
  AnnotationAnnotatedResourcesEnum,
  AnnotationEventTrackingSourcesEnum,
  AnnotationLinkedResourcesEnum,
  SelectionAnnotationTemplate,
} from '../../../../libs/shared/core/src/models/api/annotationTypes';
import {canLocationAcceptMoreAnnotations} from '../../../../libs/shared/core/src/models/helpers/annotationHelpers';
import {blendColors} from '../../../../libs/shared/core/src/models/helpers/colorHelpers';
import {DataEventActionsEnum} from '../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {ViewerDataEventObject} from '../../../../libs/shared/core/src/models/tracking/objectNames/viewerDataEventObject';
import {useDataEventTracking} from '../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../libs/shared/core/src/services/tracking/tracking';
import {
  NewPanelId,
  RViewerPanelId,
  ViewerPanelId,
} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {useLayer} from '../../../../libs/shared/ui/src/components/layers/layerContext';
import {Anchor} from '../../../../libs/shared/ui/src/components/reposition/repositionHelpers';
import {useClipboard} from '../../../../libs/shared/ui/src/hooks/clipboardHookHelpers';
import {ThemeContextType} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {
  ANNOTATION_JUMP_ANIMATION_DELAY_MS,
  ANNOTATION_JUMP_ANIMATION_DURATION_MS,
} from '../../../../srcCommon/inboxes/viewer/conversation/timeline/animationHelpers';
import {delayAsync} from '../../../helpers/asyncHelpers';
import {toDataEventPayload} from '../../../services/tracking/annotation';
import {SelectionShopControls} from '../../inboxes/viewer/conversation/timeline/selectionShop/selectionShopControls';
import {AnnotationHighlightViewerToolbar} from './annotationHighlightViewerToolbar';
import {floatingActivityDropdownId} from './floatingActivityDropdown';
import {FloatingActivityDropdownContainer} from './floatingActivityDropdownContainer';
import {FloatingMultiSelectCopyToolbarButton} from './floatingMultiSelectCopyToolbarButton';
import {useOnSelectionChange} from './useOnSelectionChange';

/*
 * Constants.
 */

const copyButtonId = 'annotations-highlight-viewer-multiselect-copy-button';

/*
 * Props.
 */

export interface AnnotationsHighlightViewerInheritableProps<T extends AnnotationModel> {
  panelId: NewPanelId;
  annotations?: ReadonlyArray<T>;
  onCreateAnnotatedComment?: (template: SelectionAnnotationTemplate) => void;
  onCreateAnnotatedLinkedConversation?: (location: SelectionLocation) => Promise<void>;
  onCreateAnnotatedLinkedDiscussion?: (location: SelectionLocation) => Promise<void>;
  hasMessageAnnotations: boolean;
}

interface AnnotationsHighlightViewerProps<T extends AnnotationModel>
  extends AnnotationsHighlightViewerInheritableProps<T> {
  conversationId: number;
  namespace: string | undefined;
  canLinkConversation: boolean;
  isAnnotatingComment: boolean;
  onJumpToActivity?: (arg: {panelId: ViewerPanelId; sourceActivityId: number}) => void;
}

export interface SelectionItem {
  id: string;
  text: string;
  anchor: Anchor;
  isMultiSelect: boolean;
  firstParentElement: HTMLElement | null;
  secondParentElement: HTMLElement | null;
}

/*
 * Style.
 */

const annotationFocus = (p: ThemeContextType) => keyframes`
  from {
    background-color: ${p.theme.annotation.focusedAnnotationBackground};
  }
  to {
    background-color: ${p.theme.annotation.commentAnnotationBackground};
  }
`;

const StyledAnnotationsDiv = styled.div<{
  $isOverflowVisible: boolean | undefined;
}>`
  flex: 1 1 auto;
  overflow: ${(p) => (p.$isOverflowVisible ? 'visible' : 'auto')};
  display: flex;
  flex-direction: column;

  ${htmlAnnotationTagName}.front_annotation {
    background-color: ${(p) => p.theme.annotation.commentAnnotationBackground};
    &[${annotationLinkedTypeAttribute}='topic'] {
      background-color: transparent;
      border: 1px solid ${(p) => p.theme.annotation.topicAnnotationBorder};
      padding: 0 1px;
      border-radius: 3px;
    }

    /* Special case for when a topic annotation is inside a comment one. */
    &[${annotationLinkedTypeAttribute}='comment'] [${annotationLinkedTypeAttribute}='topic'] {
      border-color: ${(p) =>
        blendColors(
          p.theme.annotation.commentAnnotationBackground,
          p.theme.annotation.topicAnnotationBorder,
        )};
    }

    &[${annotationLinkedTypeAttribute}] {
      &.front_annotation_focused {
        background-color: ${(p) => transparentize(0.8, p.theme.palette.blue.shade40)};
        animation-name: ${annotationFocus};
        animation-delay: ${ANNOTATION_JUMP_ANIMATION_DELAY_MS}ms;
        animation-duration: ${ANNOTATION_JUMP_ANIMATION_DURATION_MS}ms;
        animation-timing-function: linear;
      }

      &.front_annotation_selected[${annotationLinkedTypeAttribute}='comment'] {
        background-color: ${(p) => p.theme.annotation.selectedAnnotationBackground};
      }
      &.front_annotation_selected[${annotationLinkedTypeAttribute}='topic'] {
        background-color: ${(p) => p.theme.annotation.topicAnnotationSelectedBackground};
      }

      &.front_annotation_hover {
        cursor: pointer;
      }

      &.front_annotation_hover[${annotationLinkedTypeAttribute}='comment']:not(.front_annotation_selected) {
        background-color: ${(p) => p.theme.annotation.hoverAnnotationBackground};
      }
      &.front_annotation_hover[${annotationLinkedTypeAttribute}='topic']:not(.front_annotation_selected) {
        background-color: ${(p) => p.theme.annotation.topicAnnotationHoverBackground};
      }
    }
  }
`;

/*
 * Component.
 */

export const AnnotationsHighlightViewer = <T extends AnnotationModel>(
  props: PropsWithChildren<AnnotationsHighlightViewerProps<T>>,
) => {
  const {
    annotations,
    hasMessageAnnotations,
    panelId,
    conversationId,
    namespace,
    isAnnotatingComment,
    canLinkConversation,
    onJumpToActivity,
  } = props;

  const wrapperRef = useRef<HTMLDivElement>(null);
  const [currentLocation, setCurrentLocation] = useState<SelectionLocation | undefined>(undefined);
  const [selections, setSelections] = useState<ReadonlyArray<SelectionItem>>([]);
  const shouldTrackActions = isInteger(conversationId) && namespace;

  const {copy} = useClipboard();
  const {trackDataEvent} = useDataEventTracking();
  const {window} = useLayer();

  // Upon copying selected annotations (whether via clicking the copy button or keyboard shortcuts), we maintain
  // the selected state styling, but we hide the copy button.
  const [canShowCopyButton, setCanShowCopyButton] = useState(false);

  const computeSelectedAnnotationsForDataTracking = () =>
    _(selections)
      .map((selection) => selection.id)
      .map((uid) => annotations?.find((a) => a.clientSideUid === uid))
      .compact()
      .map((annotation) => toDataEventPayload(annotation))
      .value();

  const onClickToCopySelection = useEventHandler(() => {
    setCanShowCopyButton(false);
    trackDataEvent(DataEventActionsEnum.COPY, ViewerDataEventObject.ANNOTATION, {
      annotations: computeSelectedAnnotationsForDataTracking(),
      method: 'click',
    });
    const text = selections.map((annotation) => annotation.text).join(' ');
    copy(text);
  });

  const toggleSelection = useCallback((selectionItem: SelectionItem) => {
    const {
      id,
      text,
      anchor,
      isMultiSelect: newIsMultiSelect,
      firstParentElement,
      secondParentElement,
    } = selectionItem;
    setSelections((prevSelections) => {
      const isSelected = prevSelections.some((item) => item.id === id);
      if (isSelected) {
        return prevSelections.filter((item) => item.id !== id);
      }
      setCanShowCopyButton(true);
      return [
        ...map(prevSelections, (item) => ({
          ...item,
          // If we select an item using multiselect, any previously selected item also becomes multiselect.
          isMultiSelect: newIsMultiSelect ?? item.isMultiSelect,
        })),
        {id, text, anchor, isMultiSelect: newIsMultiSelect, firstParentElement, secondParentElement},
      ];
    });
  }, []);

  const clearAllSelections = useCallback(() => {
    setSelections([]);
  }, []);

  useLayoutEffect(() => {
    const selectedElements = wrapperRef.current?.querySelectorAll(`.front_annotation_selected`);
    selectedElements?.forEach((el) => {
      el.classList.remove('front_annotation_selected', 'front_annotation_hover');
    });

    selections.forEach((s) => {
      const annotationElements = wrapperRef.current?.querySelectorAll(
        `[${annotationHtmlAttribute}="${s.id}"]`,
      );

      annotationElements?.forEach((el) => {
        el.classList.add('front_annotation_selected');
      });
    });
  }, [selections]);

  const onJumpToAnnotationActivity = useCallback(
    (uid: string) => {
      const annotation = props.annotations?.find((a) => a.clientSideUid === uid);
      if (!annotation) {
        return;
      }
      const annotationActivityId = maybeFindAnnotationLinkedActivityId(annotation);
      if (!annotationActivityId || !RViewerPanelId.is(panelId)) {
        return;
      }

      onJumpToActivity?.({panelId, sourceActivityId: annotationActivityId});
    },
    [onJumpToActivity, panelId, props.annotations],
  );

  const baseTrackingProps = useMemo(
    () => ({
      namespace,
      conversation_id: conversationId,
      message_source: isAnnotatingComment
        ? AnnotationEventTrackingSourcesEnum.COMMENT
        : AnnotationEventTrackingSourcesEnum.EMAIL,
    }),
    [conversationId, namespace, isAnnotatingComment],
  );

  const maybeTrackTextSelected = useCallback(() => {
    if (!shouldTrackActions) {
      return;
    }

    trackInterfaceEvent(InterfaceEventsEnum.P3_MESSAGE_TEXT_HIGHLIGHT, baseTrackingProps);
  }, [baseTrackingProps, shouldTrackActions]);

  const maybeTrackClickOnCommentCta = useCallback(() => {
    if (!shouldTrackActions) {
      return;
    }

    trackInterfaceEvent(InterfaceEventsEnum.P3_MESSAGE_POPOVER_CLICK, {
      ...baseTrackingProps,
      cta_type: 'comment',
    });
  }, [baseTrackingProps, shouldTrackActions]);

  const onSelectionChange = (selection: Selection | null, hasMetaKeyPressed: boolean) => {
    if (hasMetaKeyPressed || !hasMessageAnnotations) {
      setCurrentLocation(undefined);
      return false;
    }

    const location = normalizeAndCreateSelectionLocationFromSelection(selection, wrapperRef.current);

    if (!canLocationAcceptMoreAnnotations(location, annotations)) {
      setCurrentLocation(undefined);
      return false;
    }
    return true;
  };

  useOnSelectionChange({
    targetRef: wrapperRef,
    onSelectionChange,
    onSelectionDone: async (hasMetaKeyPressed) => {
      // Make sure we're not capturing an old selection (when unselecting, we're sometimes getting the previous (non-empty) selection)
      await delayAsync(0);

      const selection = wrapperRef.current?.ownerDocument?.getSelection();

      if (!selection || !onSelectionChange(selection, hasMetaKeyPressed)) {
        return;
      }

      const location = normalizeAndCreateSelectionLocationFromSelection(selection, wrapperRef.current);

      // Only track when we have a selection.
      if (location) {
        maybeTrackTextSelected();
      }

      setCurrentLocation(location);
    },
  });

  const onAnnotationClick = useEventHandler((event: FrontMouseEvent) => {
    const elementTarget = event.target;
    if (!isElement(elementTarget)) {
      return;
    }

    const sourceAnnotationUid = parseAnnotationUidAtClick(event, wrapperRef);

    if (!sourceAnnotationUid) {
      // If the click is on the copy button or active annotation, don't clear all selections.
      if (
        elementTarget.closest(`#${copyButtonId}`) ||
        elementTarget.closest(`#${floatingActivityDropdownId}`)
      ) {
        return;
      }

      clearAllSelections();
      return;
    }

    const selectedAnnotation = annotations?.find((a) => a.clientSideUid === sourceAnnotationUid);

    if (!selectedAnnotation) {
      return;
    }

    // A draft quote comment annotation (no activityId yet) should not be selectable.
    const isDraftAnnotation =
      selectedAnnotation.linkedResource.type === AnnotationLinkedResourcesEnum.COMMENT &&
      !selectedAnnotation.linkedResource.commentActivityId;

    if (isDraftAnnotation) {
      return;
    }

    event.preventDefault();

    const {parentElement: firstParentElement} = elementTarget;
    const secondParentElement = isElement(firstParentElement) ? firstParentElement.parentElement : null;

    const toggleAnnotationSelection = ({isMultiSelect}: {isMultiSelect: boolean}) => {
      const annotationRect = wrapperRef.current
        ?.querySelector(`[${annotationHtmlAttribute}="${sourceAnnotationUid}"]`)
        ?.getBoundingClientRect();

      if (!annotationRect) {
        throw new Error('Should not happen.');
      }

      toggleSelection({
        id: selectedAnnotation.clientSideUid,
        text: selectedAnnotation.text,
        anchor: () => annotationRect,
        isMultiSelect,
        firstParentElement,
        secondParentElement,
      });
    };

    // Hold Cmd/Ctrl to enter multiselect mode
    if (hasMetaKey(event)) {
      toggleAnnotationSelection({isMultiSelect: true});
      return;
    }

    trackDataEvent(
      DataEventActionsEnum.CLICK,
      ViewerDataEventObject.ANNOTATION,
      toDataEventPayload(selectedAnnotation),
    );

    // Clear all selections because we can only have one selected item at once when not in multiselect.
    clearAllSelections();

    toggleAnnotationSelection({isMultiSelect: false});

    if (isAnnotatingComment && onJumpToActivity && RViewerPanelId.is(panelId)) {
      onJumpToAnnotationActivity(sourceAnnotationUid);
    }
  });

  const onAnnotationMouseOver: (event: FrontMouseEvent) => void = useEventHandler((e) => {
    if (!isElement(e.target) || !hasMessageAnnotations) {
      return;
    }

    const annotationUid = findSourceAnnotationUid(e.target);

    if (!annotationUid) {
      return;
    }

    // Special case for tooltips
    const {parentElement} = e.target;
    const parentElementUrl = parentElement?.getAttribute('href');
    if (parentElement && parentElementUrl) {
      // Note that it's not possible for there to be an active annotation and hover tooltip on a separate annotation at the same time.
      const activeAnnotation = selections.find((item) => !item.isMultiSelect);
      const annotation = annotations?.find((a) => a.clientSideUid === annotationUid);
      // There are two cases in which we want to prevent a hover tooltip on a link:
      // 1) it's an active annotation (which means it's selected using non-multiselect).
      // 2) it's a link where the text of the application object is identical to its URL.
      if (activeAnnotation || (annotation && parentElementUrl === annotation.text)) {
        // Stop event from bubbling because we don't want the LinkTooltips to display the hover tooltip
        // on top of the app object card.
        e.stopPropagation();
      }
    }

    // For all selected items, do not add a hover state.
    if (selections.some((item) => item.id.includes(annotationUid))) {
      return;
    }

    const contentWrapper = wrapperRef.current;

    if (!contentWrapper) {
      return;
    }

    const annotationElements = contentWrapper.querySelectorAll(
      `[${annotationHtmlAttribute}="${annotationUid}"]`,
    );
    annotationElements.forEach((el) => {
      el.classList.add('front_annotation_hover');
    });
  });

  const onAnnotationMouseOut: (event: FrontMouseEvent) => void = useEventHandler((e) => {
    if (!isElement(e.target) || !hasMessageAnnotations) {
      return;
    }

    const annotationUid = findSourceAnnotationUid(e.target);

    if (!annotationUid) {
      return;
    }

    const activeAnnotation = selections.find((item) => !item.isMultiSelect);

    // If an annotation is active, we're stopping here because we want to keep the hover state on it.
    if (annotationUid === activeAnnotation?.id) {
      return;
    }

    const contentWrapper = wrapperRef.current;

    if (!contentWrapper) {
      return;
    }

    const annotationElements = contentWrapper.querySelectorAll(
      `[${annotationHtmlAttribute}="${annotationUid}"]`,
    );
    annotationElements.forEach((el) => {
      el.classList.remove('front_annotation_hover');
    });
  });

  const maybeRenderToolbar = () => {
    if (!currentLocation) {
      return null;
    }

    const close = () => {
      setCurrentLocation(undefined);
      clearAllSelections();
    };

    const onCreateAnnotatedComment = (annotatedResourceType: AnnotationAnnotatedResourcesEnum) => {
      if (props.onCreateAnnotatedComment) {
        maybeTrackClickOnCommentCta();
        props.onCreateAnnotatedComment({
          location: currentLocation,
          annotatedResourceType,
        });
      }

      close();
    };

    const onCreateAnnotatedLinkedConversation = async () => {
      await props.onCreateAnnotatedLinkedConversation?.(currentLocation);

      close();
    };

    const onCreateAnnotatedLinkedDiscussion = async () => {
      await props.onCreateAnnotatedLinkedDiscussion?.(currentLocation);

      close();
    };

    const onKeyboardCopy = () => {
      copySelectionToClipboard(window);

      // Close the toolbar on copy so we don't get in the way.
      close();
    };

    return (
      <AnnotationHighlightViewerToolbar
        isAnnotatingComment={isAnnotatingComment}
        canLinkConversation={canLinkConversation}
        currentLocation={currentLocation}
        onKeyboardCopy={onKeyboardCopy}
        onRequestClose={close}
        onCreateAnnotatedComment={onCreateAnnotatedComment}
        onCreateAnnotatedLinkedConversation={onCreateAnnotatedLinkedConversation}
        onCreateAnnotatedLinkedDiscussion={onCreateAnnotatedLinkedDiscussion}
      />
    );
  };

  const maybeRenderMultiSelectCopyButton = () => {
    if (!canShowCopyButton) {
      return null;
    }
    const multiSelectedItems = selections.filter((selection) => selection.isMultiSelect);
    if (multiSelectedItems.length === 0) {
      return null;
    }
    const mostRecentlySelectedItem = last(selections);

    if (!mostRecentlySelectedItem) {
      return null;
    }

    const {anchor} = mostRecentlySelectedItem;

    const onRequestClose = () => {
      setCanShowCopyButton(false);
    };

    return (
      <div id={copyButtonId}>
        <FloatingMultiSelectCopyToolbarButton
          onRequestClose={onRequestClose}
          onClick={onClickToCopySelection}
          label={
            multiSelectedItems.length === 1 ? (
              <Trans id="annotations-multiselect-copy">Copy</Trans>
            ) : (
              <Trans id="annotations-multiselect-copyall">Copy all</Trans>
            )
          }
          anchor={anchor}
        />
      </div>
    );
  };

  const maybeRenderActiveAnnotation = () => {
    // Only a non-multiselect annotation is considered active.
    // It's only possible to have one non-multiselect item selected at once.
    const activeAnnotation = selections.find((item) => !item.isMultiSelect);
    if (!activeAnnotation || isAnnotatingComment) {
      return null;
    }

    const onRequestClose = () => {
      clearAllSelections();
    };

    const contentWrapper = wrapperRef.current;

    if (!contentWrapper) {
      return null;
    }

    // Nested annotation elements.
    // At most, it's possible to have three layers where the structure is as follows
    // <URL <Comment annotation<Topic Annotation>>> and it's possible that any of the elements are omitted,
    // but there has to be at least one annotation.

    // The active annotation element is either a topic or comment annotation
    const activeAnnotationElement = first(
      contentWrapper.querySelectorAll(`[${annotationHtmlAttribute}="${activeAnnotation.id}"]`),
    );

    // The first parent element is either a comment annotation or a URL
    const firstParentFloatingActivity = computeParentElementFloatingActivity(
      activeAnnotationElement,
      activeAnnotation.firstParentElement,
      false,
    );

    // The second parent element can only be a URL
    const secondParentFloatingActivity = computeParentElementFloatingActivity(
      activeAnnotationElement,
      activeAnnotation.secondParentElement,
      true,
    );

    const activeFloatingActivities: ReadonlyArray<FloatingActivity> = compact([
      {annotationUid: activeAnnotation.id, type: FloatingActivityTypesEnum.ANNOTATION},
      firstParentFloatingActivity,
      secondParentFloatingActivity,
    ]);

    return (
      <FloatingActivityDropdownContainer
        panelId={panelId}
        floatingActivities={activeFloatingActivities}
        anchor={activeAnnotation.anchor}
        placement="bottom-start"
        onRequestClose={onRequestClose}
      />
    );
  };

  if (!hasMessageAnnotations) {
    return (
      <>
        <StyledAnnotationsDiv ref={wrapperRef} $isOverflowVisible={props.isAnnotatingComment}>
          {props.children}
        </StyledAnnotationsDiv>
        <SelectionShopControls
          selections={selections}
          toggleSelection={toggleSelection}
          clearAllSelections={clearAllSelections}
          selectedAnnotationsForDataTracking={computeSelectedAnnotationsForDataTracking()}
          setCanShowCopyButton={setCanShowCopyButton}
        />
      </>
    );
  }

  return (
    <>
      <StyledAnnotationsDiv
        ref={wrapperRef}
        onMouseOver={onAnnotationMouseOver}
        onMouseOut={onAnnotationMouseOut}
        $isOverflowVisible={props.isAnnotatingComment}
        onClick={onAnnotationClick}
      >
        {props.children}
        {maybeRenderToolbar()}
        {maybeRenderActiveAnnotation()}
        {maybeRenderMultiSelectCopyButton()}
      </StyledAnnotationsDiv>
      <SelectionShopControls
        selections={selections}
        toggleSelection={toggleSelection}
        clearAllSelections={clearAllSelections}
        selectedAnnotationsForDataTracking={computeSelectedAnnotationsForDataTracking()}
        setCanShowCopyButton={setCanShowCopyButton}
      />
    </>
  );
};

function findSourceAnnotationUid(element: Element | undefined | null) {
  return element?.getAttribute(annotationHtmlAttribute) ?? undefined;
}

function parseAnnotationUidAtClick(event: FrontMouseEvent, wrapperRef: RefObject<HTMLDivElement>) {
  const elementTarget = event.target;
  if (!isElement(elementTarget)) {
    return undefined;
  }

  if (!isSelectionCollapsed(wrapperRef.current)) {
    return undefined;
  }

  // Grab the uid of the parent of the target.
  const parentSourceAnnotationUid = findSourceAnnotationUid(elementTarget.parentElement);

  // Grab the uid of the next sibling of the parent of the target.
  const siblingSourceAnnotationUid = findSourceAnnotationUid(elementTarget.parentElement?.nextElementSibling);

  // Grab the uid of the target.
  const targetSourceAnnotationUid = findSourceAnnotationUid(elementTarget);
  const targetIsInsideAnotherAnnotation =
    isAnnotationUid(targetSourceAnnotationUid) &&
    isAnnotationUid(parentSourceAnnotationUid) &&
    targetSourceAnnotationUid !== parentSourceAnnotationUid;
  const targetIsTheSameAsSibling =
    isAnnotationUid(targetSourceAnnotationUid) &&
    isAnnotationUid(siblingSourceAnnotationUid) &&
    targetSourceAnnotationUid === siblingSourceAnnotationUid;

  const sourceAnnotationUid =
    targetIsInsideAnotherAnnotation && targetIsTheSameAsSibling
      ? parentSourceAnnotationUid
      : targetSourceAnnotationUid;

  return isAnnotationUid(sourceAnnotationUid) ? sourceAnnotationUid : undefined;
}
